// src/components/auth/ForgotPasswordForm.jsx
import React, { useState, useRef, useContext } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const ForgotPasswordForm = ({ onBack, onVerify }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const emailRef = useRef(null);
  
  // Enhanced email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email address is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address (e.g., name@company.com)';
    }
    if (email.length > 254) {
      return 'Email address is too long';
    }
    return null;
  };

  const clearErrors = () => {
    setErrors({});
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    clearErrors();
  };

  const getErrorMessage = (error) => {
    // Handle specific error messages from backend
    if (error.includes('Email not found')) {
      return 'No account found with this email address. Please check your email or sign up for a new account.';
    }
    if (error.includes('Invalid email')) {
      return 'Please enter a valid email address format.';
    }
    if (error.includes('Email is required')) {
      return 'Email address is required to reset your password.';
    }
    if (error.includes('Unable to send email')) {
      return 'We\'re having trouble sending emails right now. Please try again in a few minutes.';
    }
    if (error.includes('Service temporarily unavailable')) {
      return 'Our system is temporarily unavailable. Please try again shortly.';
    }
    if (error.includes('Database operation failed')) {
      return 'Temporary system issue. Please try again in a moment.';
    }
    if (error.includes('reset token')) {
      return 'We encountered an issue generating your reset token. Please try again.';
    }
    if (error.includes('timeout') || error.includes('Network Error')) {
      return 'Connection timeout. Please check your internet connection and try again.';
    }
    if (error.includes('Failed to fetch')) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    
    // Default error message
    return error || 'An unexpected error occurred. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearErrors();
    
    // Validate email
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      showToast(emailError, 'error');
      emailRef.current?.focus();
      return;
    }
    
    setIsLoading(true);

    try {

      const response = await api.post(API_CONFIG.AUTH.FORGOT_PASSWORD, { email });
      

      if (response.data && response.data.success) {
        const successMessage = response.data.message || 'Password reset token has been sent to your email!';

        showToast(successMessage, 'success');
        
        // Store email for verification step if needed
        try {
          localStorage.setItem('resetEmail', email);
         
        } catch (storageError) {
 
          // Continue without storage - it's not critical
        }
        
        // Move to verification step if callback provided
        if (onVerify) {
   
          onVerify(email);
        }
      } else {
        // Handle API response with error
        const errorMessage = response.data?.error || response.data?.message || 'Could not process your request';
        const userFriendlyError = getErrorMessage(errorMessage);
        
        console.error('Password reset failed:', errorMessage);
        showToast(userFriendlyError, 'error');
        setErrors({ submit: userFriendlyError });
        
        // Focus on email field for correction
        emailRef.current?.focus();
      }
    } catch (error) {


      let errorMessage = 'An unexpected error occurred. Please try again.';

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const serverError = error.response.data?.error || error.response.data?.message;
        errorMessage = getErrorMessage(serverError || `Server error (${error.response.status})`);
        
      } else if (error.request) {
        // Request was made but no response received
        if (error.code === 'ECONNABORTED') {
          errorMessage = 'Request timeout. Please check your internet connection and try again.';
        } else if (error.message.includes('Network Error')) {
          errorMessage = 'Network connection failed. Please check your internet connection and try again.';
        } else {
          errorMessage = 'Unable to reach the server. Please check your connection and try again.';
        }
      } else if (error.message) {
        // Other errors
        errorMessage = getErrorMessage(error.message);
      }

      showToast(errorMessage, 'error');
      setErrors({ submit: errorMessage });

      // Focus on email field for user to retry
      setTimeout(() => {
        emailRef.current?.focus();
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    clearErrors();
    if (email) {
      handleSubmit(new Event('submit'));
    } else {
      emailRef.current?.focus();
    }
  };

  return (
    <div className="forgot-password-form">
      <button
        type="button"
        className="flex items-center text-blue-600 hover:text-blue-800 mb-4 transition-colors disabled:opacity-50"
        onClick={onBack}
        disabled={isLoading}
      >
        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        Back to Login
      </button>

      <div className="text-center mb-2">
        <svg className="w-16 h-16 mx-auto text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <h2 className="text-2xl font-bold  mb-2">Reset Password</h2>
        <p className="text-gray-600 mb-6">
          Enter your email address and we'll send you a token to reset your password.
        </p>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            ref={emailRef}
            id="email"
            type="email"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
              errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="Enter your registered email address"
            value={email}
            onChange={handleEmailChange}
            required
            autoComplete="email"
            disabled={isLoading}
            aria-describedby={errors.email ? "email-error" : undefined}
          />
          {errors.email && (
            <p id="email-error" className="mt-2 text-sm text-red-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {errors.email}
            </p>
          )}
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 flex items-center">
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {errors.submit}
            </p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-2 text-sm text-red-700 hover:text-red-800 underline focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
            >
              Try again
            </button>
          </div>
        )}
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending Reset Token...
            </>
          ) : (
            'Send Reset Token'
          )}
        </button>
      </form>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          Didn't receive the token?
        </h3>
        <ul className="text-xs text-blue-600 space-y-1">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            Check your spam or junk folder
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            Make sure you entered the correct email address
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            Wait a few minutes and try again
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            Contact support if the problem persists
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;